/** Valid account_type enum values matching the DB constraint */
export const ACCOUNT_TYPES = ['checking', 'savings', 'credit_card', 'line_of_credit', 'other'] as const

/** Account type union derived from DB enum */
export type AccountType = (typeof ACCOUNT_TYPES)[number]

/**
 * DCP-371: account types that are EXCLUDED from the Bank Balance aggregates
 * (Bank Balance / Book Balance tiles and their coverage warning) and from the
 * "Bank Balance Update" table.
 *
 * A credit card carries a liability, not cash on hand, so counting its balance
 * inflated Bank Balance by money the tenant does not have. The rule is applied
 * on READ only — the rows stay in `bank_accounts` and remain fully visible and
 * editable in Settings and in the account selectors/filters outside Daily
 * Banking (Daily Banking's own transaction surfaces are separately scoped —
 * see DCP-400 / {@link DAILY_BANKING_EXCLUDED_ACCOUNT_TYPES}). It applies
 * identically to connected (LedgerSync) and manual accounts, since the
 * distinction is what the account IS, not where it came from.
 *
 * Scope is deliberately EXACTLY `credit_card` (customer decision):
 * `line_of_credit` is reported separately by the Cash Position LOC block and
 * `other` is a catch-all that may legitimately hold cash.
 */
export const BANK_BALANCE_EXCLUDED_ACCOUNT_TYPES = ['credit_card'] as const satisfies readonly AccountType[]

/**
 * DCP-400: account types that are EXCLUDED from the Daily Banking page's
 * transaction surfaces.
 *
 * WHY. A credit card charge is a liability / payable, not cash moving through
 * the bank — it must not appear among Daily Banking transactions, which are
 * meant to reflect actual cash activity.
 *
 * READ-SIDE ONLY. The rows stay in `transactions` and the accounts stay fully
 * visible and editable in Settings and in every other account selector; only
 * Daily Banking's own reads are narrowed.
 *
 * SOURCE-AGNOSTIC: it reads `account_type`, so the rule applies identically to
 * manual and connected (LedgerSync) accounts, since the distinction is what
 * the account IS, not where it came from.
 *
 * WHERE APPLIED: the Daily Banking transactions table, its pagination count,
 * its CSV export, the Category-filter options, and the page's Account
 * dropdown.
 *
 * KNOWINGLY NOT APPLIED: the page's "Transaction by Facility by Category" chart
 * (hidden by DCP-331) is fed by the `get_facility_category_chart` RPC, which
 * carries no account-type predicate. Restoring the chart needs a SQL twin of
 * this rule (a migration), or it will show card charges above a table that
 * hides them.
 */
export const DAILY_BANKING_EXCLUDED_ACCOUNT_TYPES = ['credit_card'] as const satisfies readonly AccountType[]

/** Human-readable labels for account type enum values */
export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  checking: 'Checking',
  savings: 'Savings',
  credit_card: 'Credit Card',
  line_of_credit: 'Line of Credit',
  other: 'Other',
}
